import { type VariantProps, cva } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { uiOutlineClass } from "@/components/ui/global/outline";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";

const iconButton = cva(["flex w-fit items-center justify-center rounded-button-rounding", uiOutlineClass], {
  variants: {
    size: {
      small: "p-height-s",
      medium: "p-height-m",
      large: "p-height-l",
    },
    variant: {
      primary: [
        "bg-interactive-primary-idle",
        "text-interactive-primary-on",
        "hover:bg-interactive-primary-hover",
        "active:bg-interactive-primary-pressed",
        "focus-visible:outline-interactive-primary-focus",
        "disabled:bg-interactive-primary-disabled",
      ],
      secondary: [
        "bg-interactive-secondary-idle",
        "text-interactive-secondary-on",
        "hover:bg-interactive-secondary-hover",
        "active:bg-interactive-secondary-pressed",
        "focus-visible:outline-interactive-secondary-focus",
        "disabled:bg-interactive-secondary-disabled",
      ],
      outlined: [
        "border",
        "border-elevation-outline-1",
        "text-interactive-outlined-on",
        "hover:border-interactive-outlined-hover",
        "hover:text-interactive-outlined-hover",
        "active:border-interactive-outlined-pressed",
        "active:text-interactive-outlined-pressed",
        "focus-visible:outline-interactive-outlined-focus",
        "disabled:border-interactive-outlined-disabled",
        "disabled:text-interactive-outlined-disabled",
      ],
      transparent: [
        "bg-transparent",
        "text-text-inverted-secondary",
        "hover:bg-transparent",
        "hover:text-text-inverted-tertiary",
        "active:bg-transparent",
        "border-none",
      ],
      tertiary: [
        "!bg-elevation-surface-1 rounded-xs",
        "text-text-inverted-tertiary",
        "border",
        "border-gradient",
        "shadow-1",
        "hover:bg-elevation-surface-1",
        "active:bg-elevation-surface-1",
        "focus-visible:outline-elevation-surface-1",
      ],
    },
  },
  defaultVariants: {
    size: "medium",
    variant: "primary",
  },
});

type IconButtonVariantProps = VariantProps<typeof iconButton>;

type IconButtonProps = IconButtonVariantProps &
  Omit<AriaButtonProps, "children"> & {
    label: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    link?: LinkProps & { target?: string };
    iconClassName?: string;
    showTooltip?: boolean;
  };

export const IconButton = ({
  label,
  icon: Icon,
  variant,
  size,
  link,
  iconClassName = "h-6 w-6",
  showTooltip = false,
  ...props
}: IconButtonProps) => {
  const Component: any = link ? Link : AriaButton;

  return (
    <Component
      {...props}
      {...link}
      isDisabled={props.isDisabled}
      className={iconButton({ variant, size, className: props.className })}
      aria-label={label}
    >
      {showTooltip && (
        <Tooltip
          text={label}
          delay={500}
        >
          <CustomTooltipTrigger>
            <Icon className={iconClassName} />
          </CustomTooltipTrigger>
        </Tooltip>
      )}
      {!showTooltip && <Icon className={iconClassName} />}
    </Component>
  );
};
